import { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from './redux';
import { setDropdownData } from '../features/dropdown/dropdownSlice';
import { setSpeciesGroup } from '../features/speciesGroup/speciesGroupSlice';
import dropdownApiService, { DropdownDataResponse } from '../apiService/DropdownApiService';

export const useDropdownData = () => {
    const dispatch = useAppDispatch();
    const dropdownData = useAppSelector((state) => state.dropdown.data);
    const lastUpdated = useAppSelector((state) => state.dropdown.lastUpdated);
    const globalData = useAppSelector((state) => state.dropdown.globalData);
    const speciesGroupData = useAppSelector((state) => state.speciesGroup.data);

    /**
     * Preload all essential dropdown data and species groups after login
     */
    const preloadAllData = useCallback(async (forceRefresh: boolean = false): Promise<void> => {
        try {
            // Check if we already have data (from previous session)
            const hasExistingDropdownData = Object.keys(dropdownData).length > 0;
            const hasExistingSpeciesData = speciesGroupData && speciesGroupData.length > 0;
            // Check localStorage for persisted data
            const persistedData = localStorage.getItem('persist:root');
            const hasPersistedData = persistedData && persistedData !== '{}';
            if (!forceRefresh && hasExistingDropdownData && hasExistingSpeciesData && hasPersistedData) {
                return;
            }
            // Check network connectivity
            const isOnline = navigator.onLine;
            if (!isOnline) {
                // No fallback dropdown data, just return
                return;
            }

            const token = localStorage.getItem('authToken');
            if (!token) {
                return;
            }
            // Call GetDropdownDataByGroupApi to get ALL data at once
            const allDropdownData = await dropdownApiService.getDropdownDataByGroup();
            if (!allDropdownData || !allDropdownData.data) {
                console.error('Failed to fetch dropdown data');
                return;
            }
            // 1. Process and save fauna species groups
            const faunaSpecies = await dropdownApiService.getFaunaSpeciesGroups();
            if (faunaSpecies && faunaSpecies.length > 0) {
                dispatch(setSpeciesGroup(faunaSpecies));
            }
            // Save data for Birds (group 10)
            const birdsData = await dropdownApiService.filterDropdownDataByFaunaSpecies('birds');
            if (birdsData) {
                const completeResponse = {
                    data: birdsData.data || [],
                    count_methods: birdsData.count_methods || allDropdownData.count_methods || [],
                    ndff_gender: birdsData.ndff_gender || allDropdownData.ndff_gender || [],
                    status: birdsData.status !== undefined ? birdsData.status : true,
                    status_message: birdsData.status_message || 'Success'
                } as DropdownDataResponse;
                dispatch(setDropdownData({ 
                    speciesGroupId: '10', 
                    data: completeResponse
                }));
            }
            // Save data for Mammals (group 11, excluding bats)
            const mammalsData = await dropdownApiService.filterDropdownDataByFaunaSpecies('mammals');
            if (mammalsData) {
                const completeResponse = {
                    data: mammalsData.data || [],
                    count_methods: mammalsData.count_methods || allDropdownData.count_methods || [],
                    ndff_gender: mammalsData.ndff_gender || allDropdownData.ndff_gender || [],
                    status: mammalsData.status !== undefined ? mammalsData.status : true,
                    status_message: mammalsData.status_message || 'Success'
                } as DropdownDataResponse;
                dispatch(setDropdownData({ 
                    speciesGroupId: '11', 
                    data: completeResponse
                }));
            }
            // Save data for Bats (group 11, subgroup 30)
            const batsData = await dropdownApiService.filterDropdownDataByFaunaSpecies('bats');
            if (batsData) {
                const completeResponse = {
                    data: batsData.data || [],
                    count_methods: batsData.count_methods || allDropdownData.count_methods || [],
                    ndff_gender: batsData.ndff_gender || allDropdownData.ndff_gender || [],
                    status: batsData.status !== undefined ? batsData.status : true,
                    status_message: batsData.status_message || 'Success'
                } as DropdownDataResponse;
                dispatch(setDropdownData({ 
                    speciesGroupId: '11_30', 
                    data: completeResponse
                }));
            }
            // Save data for Others (groups 1-9)
            for (let groupId = 1; groupId <= 9; groupId++) {
                const othersData = await dropdownApiService.filterDropdownDataByFaunaSpecies('others', groupId);
                if (othersData) {
                    const completeResponse = {
                        data: othersData.data || [],
                        count_methods: othersData.count_methods || allDropdownData.count_methods || [],
                        ndff_gender: othersData.ndff_gender || allDropdownData.ndff_gender || [],
                        status: othersData.status !== undefined ? othersData.status : true,
                        status_message: othersData.status_message || 'Success'
                    } as DropdownDataResponse;
                    dispatch(setDropdownData({ 
                        speciesGroupId: groupId.toString(), 
                        data: completeResponse
                    }));
                }
            }
        } catch (error) {
            console.error('Error during data preload:', error);
        }
    }, [dispatch, dropdownData, speciesGroupData]);





    const fetchDropdownData = useCallback(async (speciesGroupId: string, speciesSubgroupId?: string) => {
        const numericSpeciesGroupId = Number(speciesGroupId);
        const numericSubgroupId = speciesSubgroupId ? Number(speciesSubgroupId) : undefined;

        // Create cache key that includes subgroup for bats
        const cacheKey = numericSubgroupId ? `${speciesGroupId}_${speciesSubgroupId}` : speciesGroupId;

        // Always use Redux/cached data if offline
        if (!navigator.onLine) {
            if (dropdownData[cacheKey]) {
                return dropdownData[cacheKey];
            } else {
                return null;
            }
        }

        // If online, use existing logic
        // First, check if we already have this data from preload
        if (dropdownData[cacheKey]) {
            return dropdownData[cacheKey];
        }

        // Check if we have fresh data (less than 1 hour old)
        const oneHour = 60 * 60 * 1000;
        const lastUpdateTime = lastUpdated[cacheKey];
        const isDataFresh = lastUpdateTime && (Date.now() - lastUpdateTime) < oneHour;

        if (dropdownData[cacheKey] && isDataFresh) {
            return dropdownData[cacheKey];
        }
        
        try {
            // First try to use the preloaded data by filtering it
            const allDropdownData = await dropdownApiService.getDropdownDataByGroup();
            let filteredResponse: Partial<DropdownDataResponse> | null = null;

            if (allDropdownData) {
                // Use the same filtering logic as preloadAllData
                if (numericSpeciesGroupId === 10) {
                    // Birds
                    filteredResponse = await dropdownApiService.filterDropdownDataByFaunaSpecies('birds');
                } else if (numericSpeciesGroupId === 11) {
                    if (numericSubgroupId === 30) {
                        // Bats
                        filteredResponse = await dropdownApiService.filterDropdownDataByFaunaSpecies('bats');
                    } else {
                        // Mammals (excluding bats)
                        filteredResponse = await dropdownApiService.filterDropdownDataByFaunaSpecies('mammals');
                    }
                } else if (numericSpeciesGroupId >= 1 && numericSpeciesGroupId <= 9) {
                    // Others
                    filteredResponse = await dropdownApiService.filterDropdownDataByFaunaSpecies('others', numericSpeciesGroupId);
                }
            }
            
            if (filteredResponse) {
                // Convert Partial<DropdownDataResponse> to DropdownDataResponse for Redux store
                const completeResponse = {
                    data: filteredResponse.data || [],
                    count_methods: filteredResponse.count_methods || allDropdownData?.count_methods || [],
                    ndff_gender: filteredResponse.ndff_gender || allDropdownData?.ndff_gender || [],
                    status: filteredResponse.status || true,
                    status_message: filteredResponse.status_message || 'Success'
                } as DropdownDataResponse;
                
                dispatch(setDropdownData({ 
                    speciesGroupId: cacheKey, 
                    data: completeResponse 
                }));
                return completeResponse;
            } else {
                // Fallback to the old method if filtering fails
                const response = await dropdownApiService.getFilteredDropdownDataBySpeciesGroupId(numericSpeciesGroupId, numericSubgroupId);
                
                if (response) {
                    const completeResponse = {
                        data: response.data || [],
                        count_methods: response.count_methods || [],
                        ndff_gender: response.ndff_gender || [],
                        status: response.status || false,
                        status_message: response.status_message || ''
                    } as DropdownDataResponse;
                    
                    dispatch(setDropdownData({ 
                        speciesGroupId: cacheKey, 
                        data: completeResponse 
                    }));
                    return completeResponse;
                } else {
                    return null;
                }
            }
        } catch (error) {
            console.error(`Error fetching dropdown data for species group ${cacheKey}:`, error);
            if (dropdownData[cacheKey]) {
                return dropdownData[cacheKey];
            }
            return null;
        }
    }, [dispatch, dropdownData, lastUpdated]);

    const getDropdownDataForSpeciesGroup = useCallback((speciesGroupId: string, speciesSubgroupId?: string) => {
        // Create cache key that includes subgroup for bats
        const cacheKey = speciesSubgroupId ? `${speciesGroupId}_${speciesSubgroupId}` : speciesGroupId;
        
        const data = dropdownData[cacheKey];
        if (data) {
            return data;
        } else {
            return null;
        }
    }, [dropdownData]);

    return {
        fetchDropdownData,
        getDropdownDataForSpeciesGroup,
        globalData,
        speciesGroupData,
        preloadAllData, // New method for comprehensive preloading
    };
};